import { useState } from 'react';
import { useQuery } from 'react-query';
/** Components */
import { PageHelper } from 'Components/PageHelper';
import { DefaultTable } from 'Components/DefaultTable';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { tableHeader } from './table-header.js';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
/** Services */
import { getAssetsShortPaged } from 'services/assets-service';
/** State Managers */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
} from '@mui/material';
import { getUsersName } from 'services/users-service';
import { Controller, useForm } from 'react-hook-form';
import { Card } from 'Components/Card';
import { Column, Row } from 'Components/Grid';
import { CustomTextField } from 'Components/Form/styles';
import { InputLoader } from 'Components/PageLoader/Input';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { callArchiveAction, callDeleteAction, callUnarchiveAction } from './actions';
import { ModalConfirm } from 'Components/ModalConfirm';

export const Assets = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnarchiveModal, setOpenUnarchiveModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToUnarchive, setItemToUnarchive] = useState(null);
  const [page, setPage] = useState(1);

  const { register, control, reset, watch, getValues } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      location: '',
      type: '',
      user: '',
      archived: false,
      pii: 'Todos',
    },
  });

  const getContentSearch = () => {
    const dados = getValues();
    let queryString = `&archived=${dados.archived}&pii=${dados.pii}`;
    if (dados.name !== '') {
      queryString += `&name=${dados.name}`;
    }
    if (dados.criticity !== '') {
      queryString += `&criticity=${dados.criticity}`;
    }
    if (dados.location !== '') {
      queryString += `&location=${dados.location}`;
    }
    if (dados.type !== '') {
      queryString += `&type=${dados.type}`;
    }
    if (dados.user !== '') {
      queryString += `&user=${dados.user}`;
    }

    return queryString;
  };

  const { isLoading: userLoading, data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const {
    isLoading,
    error,
    data: pagedAssets,
  } = useQuery(['getAssets', refetch, page], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];

    const queryContent = getContentSearch();
    const result = await getAssetsShortPaged(selectedTenant.slug, page, 15, queryContent);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const onArchive = item => {
    setItemToArchive(item);
    setOpenArchiveModal(true);
  };

  const onUnarchive = item => {
    setItemToUnarchive(item);
    setOpenUnarchiveModal(true);
  };

  const onCloseArchiveModal = () => {
    setOpenArchiveModal(false);
    setItemToArchive(null);
  };

  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setItemToDelete(null);
  };

  const onCloseUnarchiveModal = () => {
    setOpenUnarchiveModal(false);
    setItemToUnarchive(null);
  };

  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseDeleteModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmArchiveModal = async () => {
    try {
      await callArchiveAction(itemToArchive, onCloseArchiveModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseArchiveModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmUnarchiveModal = async () => {
    try {
      await callUnarchiveAction(itemToUnarchive, onCloseUnarchiveModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseUnarchiveModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <Container title="Ativos" breadcrumb="Mapeamento / Ativos" showButtonAdd linkPage="/assets" data-tour="page-title">
      <div data-tour="info-card">
      <Information
        title={'Ativos'}
        description={
          <p className="m-0">
            Os ativos ficam nos “bastidores”, suportando toda a nossa operação. Eles podem ser de três tipos:
            <br />
            <br />
            &emsp;• Pessoas: times, gestores, colaboradores, etc
            <br />
            &emsp;• Tecnologia: computadores, tablets, smartphones, servidores, bancos de dados, etc
            <br />
            &emsp;• Ambiente: datacenters, ambientes de nuvem (AWS, Google, Azure, etc), escritórios, etc
          </p>
        }
        style={{
          marginTop: '24px',
        }}
      />
      </div>
      {!error && (
        <>
          <Card className="mb-2 mt-3" sx={{ padding: '24px' }} data-tour="filter-card">
            <Row className="mb-3" sx={{ alignItems: 'center' }}>
              <Column xs={12} md={6}>
                <div data-tour="filter-name" style={{ width: '100%' }}>
                <CustomTextField
                  {...register('name')}
                  label="Nome"
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                </div>
              </Column>
              <Column xs={12} md={4}>
                {!userLoading && (
                  <div data-tour="filter-user" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="user"
                    render={({ field: { onChange, value } }) => (
                      <FormControl fullWidth>
                        <InputLabel id="user-label" shrink>
                          Responsável
                        </InputLabel>
                        <Select
                          labelId="user-label"
                          id="user"
                          value={value}
                          label="Responsável"
                          onChange={onChange}
                          notched
                          sx={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            margin: '0 auto',
                            width: '100%',
                          }}
                        >
                          <MenuItem value="" key={-1}>
                            Selecione um responsável
                          </MenuItem>
                          {users
                            ?.map(user => user.name)
                            .map((item, index) => (
                              <MenuItem value={item} key={`user-${index}`}>
                                {item}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  </div>
                )}
                {userLoading && (
                  <FormControl fullWidth sx={{ margin: '0 auto' }}>
                    <InputLabel id="user-label" shrink={true}>
                      Responsável
                    </InputLabel>
                    <InputLoader height={'40px'} />
                  </FormControl>
                )}
              </Column>
              <Column xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-tour="filter-archived">
                <Controller
                  control={control}
                  name="archived"
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      value={value}
                      control={<Checkbox checked={value} onChange={onChange} name="archived" />}
                      label="Arquivado"
                      sx={{ marginLeft: 0, whiteSpace: 'nowrap' }}
                    />
                  )}
                />
              </Column>
            </Row>
            <Row className="mb-3" sx={{ alignItems: 'center' }}>
              <Column xs={12} md={3}>
                <div data-tour="filter-criticity" style={{ width: '100%' }}>
                <Controller
                  control={control}
                  name="criticity"
                  render={({ field: { onChange, value } }) => {
                    const getCriticityColor = (level) => {
                      const colors = {
                        '1': '#22c55e',
                        '2': '#84cc16',
                        '3': '#eab308',
                        '4': '#f97316',
                        '5': '#ef4444'
                      };
                      return colors[level] || '#6b7280';
                    };

                    const getCriticityLabel = (level) => {
                      const labels = {
                        '1': '1 - Muito Baixa',
                        '2': '2 - Baixa',
                        '3': '3 - Média',
                        '4': '4 - Alta',
                        '5': '5 - Muito Alta'
                      };
                      return labels[level] || level;
                    };
                    
                    return (
                      <FormControl fullWidth>
                        <InputLabel id="criticity-label" shrink>
                          Criticidade
                        </InputLabel>
                        <Select
                          labelId="criticity-label"
                          id="criticity"
                          value={value}
                          label="Criticidade"
                          onChange={onChange}
                          notched
                          sx={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            margin: '0 auto',
                            width: '100%',
                          }}
                          renderValue={(selected) => {
                            if (!selected) return 'Selecione uma criticidade';
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: getCriticityColor(selected)
                                  }}
                                />
                                <span>{getCriticityLabel(selected)}</span>
                              </div>
                            );
                          }}
                        >
                          <MenuItem value="" key={-1}>
                            Selecione uma criticidade
                          </MenuItem>
                          {['1','2','3','4','5'].map((item, index) => (
                            <MenuItem value={item} key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <div
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: getCriticityColor(item),
                                  border: '1px solid rgba(0,0,0,0.1)',
                                  marginRight: '8px'
                                }}
                              />
                              <span>{getCriticityLabel(item)}</span>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }}
                />
                </div>
              </Column>
              <Column xs={12} md={3}>
                <div data-tour="filter-type" style={{ width: '100%' }}>
                <Controller
                  control={control}
                  name="type"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="type-label" shrink>
                        Tipo
                      </InputLabel>
                      <Select
                        labelId="type-label"
                        id="type"
                        value={value}
                        label="Tipo"
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                      >
                        <MenuItem value="" key={-1}>
                          Selecione um tipo
                        </MenuItem>
                        {['Pessoa', 'Tecnologia', 'Ambiente'].map((item, index) => (
                          <MenuItem value={item} key={index}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={3}>
                <div data-tour="filter-pii" style={{ width: '100%' }}>
                <Controller
                  control={control}
                  name="pii"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="pii-label" shrink>
                        PII
                      </InputLabel>
                      <Select
                        labelId="pii-label"
                        id="pii"
                        value={value}
                        label="PII"
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                      >
                        {['Todos', 'Sim', 'Não'].map(item => (
                          <MenuItem value={item} key={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div data-tour="filter-buttons" style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '12px',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  width: '100%'
                }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    width: '120px',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    height: '36px',
                    fontWeight: 500
                  }}
                  onClick={() => {
                    reset();
                    setRefetch(!refetch);
                  }}
                >
                  Limpar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    width: '120px',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    height: '36px',
                    fontWeight: 500
                  }}
                  endIcon={<SearchOutlinedIcon />}
                  disabled={watch('typeFilter') === ''}
                  onClick={() => {
                    setRefetch(!refetch);
                  }}
                >
                  Filtrar
                </Button>
                </div>
              </Column>
            </Row>
          </Card>
        </>
      )}
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>Ocorreu um erro ...</div>}
      {!isLoading && !error && pagedAssets.data?.length === 0 && <NoData description={'Não há ativos para exibir'} />}
      {!isLoading && !error && pagedAssets.data?.length > 0 && (
        <div className="container">
          <div data-tour="table">
          <DefaultTable
            rows={tableHeader}
            cells={pagedAssets.data}
            onClickDelete={onDelete}
            onClickArchive={onArchive}
            onClickUnarchive={onUnarchive}
            showArchive
            link="assets"
          />
          </div>
          <div data-tour="pagination">
          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
            <Pagination
              count={pagedAssets.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          </div>
          <ModalConfirmDelete
            description={
              <>
                Tem certeza que deseja excluir o Ativo
                <strong>{` ${itemToDelete?.name}`}</strong>?
              </>
            }
            open={openDeleteModal}
            onClose={onCloseDeleteModal}
            onConfirm={onConfirmDeleteModal}
          />
          <ModalConfirm
            description={
              <>
                Tem certeza que deseja arquivar o Ativo <strong>{` ${itemToArchive?.name}`}</strong>?
              </>
            }
            open={openArchiveModal}
            onClose={onCloseArchiveModal}
            onConfirm={onConfirmArchiveModal}
          />
          <ModalConfirm
            description={
              <>
                Tem certeza que deseja desarquivar o Ativo
                <strong>{` ${itemToUnarchive?.name}`}</strong>?
              </>
            }
            open={openUnarchiveModal}
            onClose={onCloseUnarchiveModal}
            onConfirm={onConfirmUnarchiveModal}
          />
        </div>
      )}
      <PageHelper steps={tourSteps} tourKey="assets-list" />
    </Container>
  );
};

const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo à página de Ativos! Aqui você gerencia todos os recursos que suportam suas operações.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="page-title"] button',
    content: 'Use este botão para adicionar um novo ativo ao sistema.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="info-card"]',
    content: 'Os ativos podem ser de três tipos: Pessoas (colaboradores, gestores), Tecnologia (computadores, servidores) e Ambiente (datacenters, escritórios).',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filter-card"]',
    content: 'Use os filtros para encontrar ativos específicos. Você pode combinar múltiplos critérios de busca.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filter-name"]',
    content: 'Filtre ativos pelo nome. Digite parte do nome para buscar.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="filter-user"]',
    content: 'Filtre por responsável. Selecione o usuário que gerencia o ativo.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="filter-archived"]',
    content: 'Marque esta opção para exibir apenas ativos arquivados.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="filter-criticity"]',
    content: 'Filtre pela criticidade do ativo. A escala vai de 1 (Muito Baixa - verde) a 5 (Muito Alta - vermelho).',
    disableScrolling: false,
  },
  {
    target: '[data-tour="filter-type"]',
    content: 'Filtre pelo tipo de ativo: Pessoa, Tecnologia ou Ambiente.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="filter-pii"]',
    content: 'Filtre por PII (Personally Identifiable Information). Indica se o ativo processa dados pessoais identificáveis.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="filter-buttons"]',
    content: 'Use "Limpar" para remover todos os filtros ou "Filtrar" para aplicar os critérios selecionados.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="table"]',
    content: 'Esta tabela exibe todos os ativos cadastrados. Você pode visualizar, editar, arquivar ou excluir cada item.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="pagination"]',
    content: 'Use a paginação para navegar entre as páginas de ativos quando houver muitos resultados.',
    disableScrolling: false,
  },
  {
    target: 'body',
    content: 'Tour concluído! Agora você conhece todas as funcionalidades da página de Ativos. Explore e gerencie seus recursos com facilidade.',
    disableScrolling: true,
    placement: 'center',
  },
];
